import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Tile, EmptyStateMessage } from './Tile';
import { ContentType, shouldShowContent } from './contentTypes';
import { MeasurementTypeEnum, MeasurementSignificanceEnum } from '../../enums/measurementTypeEnum';
import { DocumentService, sharedApiClient } from '../../fetcher';
import { useAuth, useNotifications, CustomDropdown, CustomDatePicker, calendarDateFromInstant, calendarDateToEpochMillis } from '@medprompt/shared';
import { UserAbility } from '@medprompt/shared';
import AddMeasurementForm from '../AddMeasurementForm';
import {
  BLOOD_PRESSURE_DISPLAY_NAME,
  bundleBloodPressureReadings,
  getDisplayRowDate,
  getDisplayRowId,
  getDisplayRowIds,
  getDisplayRowUnit,
  getMeasurementId,
  isBloodPressurePair,
  isDisplayRowSelected,
} from '../measurements/bundleBloodPressureReadings';
import { BloodPressureValueDisplay, BloodPressureValueEditor } from '../measurements/BloodPressureValue';

const formatEpochToDdMmYyyy = (epochMillis) => {
  const d = new Date(epochMillis);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

// Enum for measurement comparators matching backend MeasurementComparator
const MeasurementComparator = {
  EQ: { value: 'EQ', display: '=', symbol: '' },
  GT: { value: 'GT', display: '>', symbol: '>' },
  LT: { value: 'LT', display: '<', symbol: '<' }
};

// Helper function to get comparator symbol for display
const getComparatorSymbol = (comparator) => {
  if (!comparator || comparator === 'EQ' || !(comparator in MeasurementComparator)) {
    return '';
  }
  return MeasurementComparator[comparator].symbol + ' ';
};

const isCodeOnlyMeasurement = (measurement) => measurement.valueType === 'NONE';

const getMeasurementSignificance = (measurement) => {
  if (measurement.significance) {
    return measurement.significance;
  }
  return MeasurementTypeEnum[measurement.type]?.significance || MeasurementSignificanceEnum.CORE.value;
};

const getDisplayRowSignificance = (row) =>
  getMeasurementSignificance(isBloodPressurePair(row) ? row.systolic.measurement : row.measurement);

const MeasurementsTab = ({ doc, isRegenerationEnabled, isEditingEnabled, handleRegenerationClick, onDocumentUpdate, shouldBundleBloodPressureReadings = false }) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const hasDocsWriteAbility = user?.authorities?.includes(UserAbility.DOCS_WRITE.key);
  const canEdit = isEditingEnabled ? isEditingEnabled() : hasDocsWriteAbility;
  const [editingMeasurementId, setEditingMeasurementId] = useState(null);
  const [editableValue, setEditableValue] = useState('');
  const [editableSecondaryValue, setEditableSecondaryValue] = useState('');
  const [editableUnit, setEditableUnit] = useState('');
  const [editableComparator, setEditableComparator] = useState('EQ');
  const [isComparatorDropdownOpen, setIsComparatorDropdownOpen] = useState(false);
  const [showAddMeasurementForm, setShowAddMeasurementForm] = useState(false);
  
  const documentManager = useMemo(() => new DocumentService(sharedApiClient), []);
  const measurementValueInputRef = useRef(null);
  const measurementEditContainerRef = useRef(null);

  const displayRows = useMemo(
    () => bundleBloodPressureReadings(doc.measurements, shouldBundleBloodPressureReadings).map((row) => ({
      ...row,
      significance: getDisplayRowSignificance(row),
    })),
    [doc.measurements, shouldBundleBloodPressureReadings]
  );

  // Focus value input when editing starts
  useEffect(() => {
    if (editingMeasurementId && measurementValueInputRef.current) {
      measurementValueInputRef.current.focus();
      measurementValueInputRef.current.select();
    }
  }, [editingMeasurementId]);

  const applyLocalMeasurementUpdates = useCallback((ids, updater) => {
    const idSet = new Set(ids);
    const updatedMeasurements = doc.measurements.map((measurement, index) => {
      const id = getMeasurementId(measurement, index);
      return idSet.has(id) ? updater(measurement, id) : measurement;
    });

    const updatedDoc = { ...doc, measurements: updatedMeasurements };
    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }
    return updatedMeasurements;
  }, [doc, onDocumentUpdate]);

  const persistSelection = useCallback((ids, isChecked) => {
    const isSelected = isChecked ? true : null;
    const isDeselected = !isChecked ? true : null;
    ids.forEach((id) => {
      documentManager.updateDocumentMeasurement(doc.documentId, id, { isSelected, isDeselected })
        .catch(error => {
          console.error('Error updating measurement selection:', error);
          addNotification(`measurement-select-error-${id}`, 'Failed to update measurement selection', { isError: true });
        });
    });
  }, [doc.documentId, documentManager, addNotification]);

  const handleDisplayRowSelectionChange = useCallback((row, isChecked) => {
    const ids = getDisplayRowIds(row);
    applyLocalMeasurementUpdates(ids, (measurement) => ({ ...measurement, isSelected: isChecked }));
    persistSelection(ids, isChecked);
  }, [applyLocalMeasurementUpdates, persistSelection]);

  // Handler for selecting all measurements
  const handleSelectAllMeasurements = useCallback((e) => {
    const checked = e.target.checked;
    
    const updatedMeasurements = doc.measurements?.map((measurement) => {
      return { ...measurement, isSelected: checked };
    });
    
    const updatedDoc = { ...doc, measurements: updatedMeasurements };
    
    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }
    
    doc.measurements?.forEach((measurement, index) => {
      const measurementId = getMeasurementId(measurement, index);
      persistSelection([measurementId], checked);
    });
  }, [doc, onDocumentUpdate, persistSelection]);

  const handleSelectGroupMeasurements = useCallback((groupEntries, checked) => {
    const groupIds = groupEntries.flatMap(getDisplayRowIds);
    applyLocalMeasurementUpdates(groupIds, (measurement) => ({ ...measurement, isSelected: checked }));
    persistSelection(groupIds, checked);
  }, [applyLocalMeasurementUpdates, persistSelection]);

  const partitionedMeasurements = useMemo(() => {
    if (!displayRows.length) {
      return { core: [], extended: [], hasExtended: false };
    }

    const hasExtended = displayRows.some((row) => row.significance === MeasurementSignificanceEnum.EXTENDED.value);

    return {
      core: displayRows.filter((row) => row.significance !== MeasurementSignificanceEnum.EXTENDED.value),
      extended: displayRows.filter((row) => row.significance === MeasurementSignificanceEnum.EXTENDED.value),
      hasExtended,
    };
  }, [displayRows]);

  const clearEditState = useCallback(() => {
    setEditingMeasurementId(null);
    setEditableValue('');
    setEditableSecondaryValue('');
    setEditableUnit('');
    setEditableComparator('EQ');
    setIsComparatorDropdownOpen(false);
  }, []);

  const handleDisplayRowClick = useCallback((row) => {
    const rowId = getDisplayRowId(row);
    if (!canEdit || editingMeasurementId === rowId) return;
    if (!isBloodPressurePair(row) && isCodeOnlyMeasurement(row.measurement)) return;

    setEditingMeasurementId(rowId);
    setIsComparatorDropdownOpen(false);
    if (isBloodPressurePair(row)) {
      setEditableValue(row.systolic.measurement.value?.toString() || '');
      setEditableSecondaryValue(row.diastolic.measurement.value?.toString() || '');
      setEditableUnit(getDisplayRowUnit(row));
      setEditableComparator('EQ');
      return;
    }

    setEditableValue(row.measurement.value?.toString() || '');
    setEditableSecondaryValue('');
    setEditableUnit(row.measurement.unit || '');
    setEditableComparator(row.measurement.comparator || 'EQ');
  }, [canEdit, editingMeasurementId]);

  const persistMeasurementFields = useCallback((measurementId, fields) => {
    documentManager.updateDocumentMeasurement(doc.documentId, measurementId, fields)
      .catch(error => {
        console.error('Error updating measurement value/unit:', error);
        addNotification(`measurement-save-error-${measurementId}`, 'Failed to save measurement changes', { isError: true });
      });
  }, [doc.documentId, documentManager, addNotification]);

  const handleDisplayRowBlur = useCallback((row) => {
    if (isBloodPressurePair(row)) {
      const originalSystolic = row.systolic.measurement.value?.toString() || '';
      const originalDiastolic = row.diastolic.measurement.value?.toString() || '';
      const originalUnit = getDisplayRowUnit(row);
      const hasChanges =
        editableValue !== originalSystolic ||
        editableSecondaryValue !== originalDiastolic ||
        editableUnit !== originalUnit;

      if (hasChanges && editableValue.trim() && editableSecondaryValue.trim()) {
        const systolicValue = parseFloat(editableValue);
        const diastolicValue = parseFloat(editableSecondaryValue);
        const unit = editableUnit || row.systolic.measurement.unit;

        applyLocalMeasurementUpdates(
          [row.systolic.id, row.diastolic.id],
          (measurement, id) => ({
            ...measurement,
            value: id === row.systolic.id
              ? (isNaN(systolicValue) ? measurement.value : systolicValue)
              : (isNaN(diastolicValue) ? measurement.value : diastolicValue),
            unit: unit || measurement.unit,
          })
        );

        persistMeasurementFields(row.systolic.id, {
          value: isNaN(systolicValue) ? null : systolicValue,
          unit: editableUnit || null,
        });
        persistMeasurementFields(row.diastolic.id, {
          value: isNaN(diastolicValue) ? null : diastolicValue,
          unit: editableUnit || null,
        });
      }

      clearEditState();
      return;
    }

    const measurement = row.measurement;
    const measurementId = row.id;
    const hasChanges = 
      editableValue !== (measurement.value?.toString() || '') ||
      editableUnit !== (measurement.unit || '') ||
      editableComparator !== (measurement.comparator || 'EQ');

    if (hasChanges && editableValue.trim()) {
      const value = parseFloat(editableValue);
      applyLocalMeasurementUpdates([measurementId], (current) => ({
        ...current,
        value: isNaN(value) ? current.value : value,
        unit: editableUnit || current.unit,
        comparator: editableComparator || 'EQ',
      }));

      persistMeasurementFields(measurementId, {
        value: isNaN(value) ? null : value,
        unit: editableUnit || null,
        comparator: editableComparator || 'EQ',
      });
    }
    
    clearEditState();
  }, [editableValue, editableSecondaryValue, editableUnit, editableComparator, applyLocalMeasurementUpdates, persistMeasurementFields, clearEditState]);

  const handleMeasurementKeyDown = useCallback((e, row) => {
    if (e.key === 'Escape') {
      if (isBloodPressurePair(row)) {
        setEditableValue(row.systolic.measurement.value?.toString() || '');
        setEditableSecondaryValue(row.diastolic.measurement.value?.toString() || '');
        setEditableUnit(getDisplayRowUnit(row));
      } else {
        setEditableValue(row.measurement.value?.toString() || '');
        setEditableUnit(row.measurement.unit || '');
        setEditableComparator(row.measurement.comparator || 'EQ');
      }
      setEditingMeasurementId(null);
      setIsComparatorDropdownOpen(false);
    }
  }, []);

  // Save and exit edit mode when clicking outside the edit container
  useEffect(() => {
    if (!editingMeasurementId) return;

    const handlePointerDownOutside = (event) => {
      if (measurementEditContainerRef.current?.contains(event.target)) {
        return;
      }

      const row = displayRows.find((displayRow) => getDisplayRowId(displayRow) === editingMeasurementId);
      if (!row) return;

      handleDisplayRowBlur(row);
    };

    document.addEventListener('mousedown', handlePointerDownOutside);
    return () => document.removeEventListener('mousedown', handlePointerDownOutside);
  }, [editingMeasurementId, displayRows, handleDisplayRowBlur]);

  const handleAddMeasurement = useCallback((newMeasurement) => {
    documentManager.updateDocument(doc.documentId, { measurements: [newMeasurement] })
      .then(() => {
        // SILENT strategy: no success notification for inline edits
        // SSE will trigger data refresh for all users
        setShowAddMeasurementForm(false);
      })
      .catch(error => {
        console.error('Error adding measurement:', error);
        addNotification(`measurement-add-error-${doc.documentId}`, 'Failed to add measurement', { isError: true });
      });
  }, [doc.documentId, documentManager, addNotification]);

  const handleDisplayRowDateChange = useCallback((row, calendarDateStr) => {
    const epochMillis = calendarDateStr ? calendarDateToEpochMillis(calendarDateStr) : null;
    const ids = getDisplayRowIds(row);

    applyLocalMeasurementUpdates(ids, (measurement) => ({ ...measurement, date: epochMillis }));

    ids.forEach((id) => {
      documentManager.updateDocumentMeasurement(doc.documentId, id, { date: epochMillis })
        .catch(error => {
          console.error('Error updating measurement date:', error);
          addNotification(`measurement-date-error-${id}`, 'Failed to update measurement date', { isError: true });
        });
    });
  }, [applyLocalMeasurementUpdates, doc.documentId, documentManager, addNotification]);

  const renderMeasurementCode = (measurement, codeOnly) => {
    if (!measurement.code) return null;
    return (
      <div className={`measurement-code${codeOnly ? ' measurement-code-primary' : ''}`}>
        <div className="measurement-code-value">
          {measurement.code.code}
        </div>
        {measurement.code.terms && measurement.code.terms.length > 0 && (
          <div className="measurement-code-term">
            {measurement.code.terms[0]}
          </div>
        )}
      </div>
    );
  };

  const renderDisplayRow = (row) => {
    const rowId = getDisplayRowId(row);
    const significance = row.significance;
    const isExtended = significance === MeasurementSignificanceEnum.EXTENDED.value;
    const isPair = isBloodPressurePair(row);
    const measurement = isPair ? row.systolic.measurement : row.measurement;
    const codeOnly = !isPair && isCodeOnlyMeasurement(measurement);
    const date = getDisplayRowDate(row);
    const typeLabel = isPair
      ? BLOOD_PRESSURE_DISPLAY_NAME
      : (MeasurementTypeEnum[measurement.type]?.displayName || measurement.type);

    return (
      <div
        key={rowId}
        className={`document-ai-item measurement-item${isExtended ? ' significance-extended' : ''}${codeOnly ? ' value-type-none' : ''}`}
      >
        <div className="measurement-details">
          <div className="measurement-header-row">
            <div className="measurement-type code-id">
              {typeLabel}
            </div>
            {date && (
              <span className="code-timestamp measurement-code-timestamp">{formatEpochToDdMmYyyy(date)}</span>
            )}
          </div>

          {!codeOnly && (
            <div className="measurement-value-container">
              <div className="measurement-value-row">
                {editingMeasurementId === rowId ? (
                  isPair ? (
                    <BloodPressureValueEditor
                      containerRef={measurementEditContainerRef}
                      systolicInputRef={measurementValueInputRef}
                      systolicValue={editableValue}
                      diastolicValue={editableSecondaryValue}
                      unit={editableUnit}
                      onSystolicChange={setEditableValue}
                      onDiastolicChange={setEditableSecondaryValue}
                      onUnitChange={setEditableUnit}
                      onKeyDown={(e) => handleMeasurementKeyDown(e, row)}
                    />
                  ) : (
                    <div
                      ref={measurementEditContainerRef}
                      className="measurement-edit-container"
                      onKeyDown={(e) => handleMeasurementKeyDown(e, row)}
                    >
                      <CustomDropdown
                        selected={MeasurementComparator[editableComparator]?.display || '='}
                        options={Object.values(MeasurementComparator)}
                        isOpen={isComparatorDropdownOpen}
                        toggleDropdown={() => setIsComparatorDropdownOpen(!isComparatorDropdownOpen)}
                        onSelect={(option) => {
                          setEditableComparator(option.value);
                          setIsComparatorDropdownOpen(false);
                        }}
                        className="measurement-comparator-dropdown"
                        width="80px"
                      />
                      <input
                        ref={measurementValueInputRef}
                        type="number"
                        step="any"
                        value={editableValue}
                        onChange={(e) => setEditableValue(e.target.value)}
                        className="measurement-value-input"
                        placeholder="Value"
                      />
                      <input
                        type="text"
                        value={editableUnit}
                        onChange={(e) => setEditableUnit(e.target.value)}
                        className="measurement-unit-input"
                        placeholder="Unit"
                      />
                    </div>
                  )
                ) : isPair ? (
                  <BloodPressureValueDisplay
                    systolicMeasurement={row.systolic.measurement}
                    diastolicMeasurement={row.diastolic.measurement}
                    canEdit={canEdit}
                    onClick={() => handleDisplayRowClick(row)}
                  />
                ) : (
                  <div
                    className={`measurement-value-display ${canEdit ? 'measurement-value-editable' : ''}`}
                    onClick={() => handleDisplayRowClick(row)}
                    onMouseMove={(e) => {
                      const tooltip = e.currentTarget.querySelector('.edit-tooltip');
                      if (tooltip) {
                        tooltip.style.left = `${e.clientX + 10}px`;
                        tooltip.style.top = `${e.clientY + 10}px`;
                      }
                    }}
                  >
                    {getComparatorSymbol(measurement.comparator)}{measurement.value} {measurement.unit}
                    {canEdit && <div className="edit-tooltip">Click to edit measurement</div>}
                  </div>
                )}
              </div>
            </div>
          )}

          {isPair ? (
            <>
              {renderMeasurementCode(row.systolic.measurement, false)}
              {renderMeasurementCode(row.diastolic.measurement, false)}
            </>
          ) : (
            renderMeasurementCode(measurement, codeOnly)
          )}
        </div>
        <div className="measurement-actions">
          <div className="measurement-action-cell">
            <input
              type="checkbox"
              className="outcome-checkbox measurement-checkbox"
              id={`checkbox_${rowId}`}
              checked={isDisplayRowSelected(row)}
              onChange={(e) => handleDisplayRowSelectionChange(row, e.target.checked)}
              disabled={!canEdit}
            />
          </div>
          {canEdit && (
            <div className="measurement-action-cell">
              <CustomDatePicker
                compact
                compactAlign="left"
                value={date ? calendarDateFromInstant(date) : null}
                onChange={(dateStr) => handleDisplayRowDateChange(row, dateStr)}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMeasurementGroup = (heading, entries) => (
    <div className="measurement-group">
      <div className="code-suggest-group-header">
        <h4 className="code-suggest-group-heading">{heading}</h4>
        {canEdit && entries.length > 0 && (
          <input
            type="checkbox"
            className="code-checkbox measurement-checkbox"
            onChange={(e) => handleSelectGroupMeasurements(entries, e.target.checked)}
            disabled={!canEdit}
          />
        )}
      </div>
      <div className="measurement-group-items">
        {entries.map(renderDisplayRow)}
      </div>
    </div>
  );

  // Render the measurement items
  const renderMeasurements = () => {
    if (!doc.measurements) {
      return <EmptyStateMessage message="No measurements found" />;
    }

    if (doc.measurements.length === 0) {
      return <EmptyStateMessage message="No measurements found" />;
    }

    if (!partitionedMeasurements.hasExtended) {
      return displayRows.map(renderDisplayRow);
    }

    return (
      <>
        {renderMeasurementGroup('QOF & monitoring', partitionedMeasurements.core)}
        {renderMeasurementGroup('Broad panel', partitionedMeasurements.extended)}
      </>
    );
  };

  // Calculate available measurement types (not already in doc.measurements)
  const availableTypes = useMemo(() => 
    Object.keys(MeasurementTypeEnum).filter(type => 
      MeasurementTypeEnum[type].valueType !== 'NONE' &&
      !doc.measurements?.some(m => m.type === type)
    ),
    [doc.measurements]
  );

  // Create the main tile
  const measurementsTile = (
    <Tile
      doc={doc}
      documentId={doc.documentId} 
      contentType={ContentType.MEASUREMENTS_STATUS} 
      title="Patient Measurements"
      condition={shouldShowContent(doc.measurements, doc.status)}
      isEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false} 
      onRegenerateClick={handleRegenerationClick}
      showContentOnError
      headerContent={
        doc.measurements && doc.measurements.length > 0 && canEdit && !partitionedMeasurements.hasExtended &&
          <input 
            type="checkbox" 
            className="code-checkbox measurement-checkbox" 
            onChange={handleSelectAllMeasurements}
            disabled={!canEdit}
          />
      }
    >
      {canEdit && availableTypes.length > 0 && !showAddMeasurementForm && (
        <button 
          className="add-measurement-button"
          onClick={() => setShowAddMeasurementForm(true)}
        >
          + Add Measurement
        </button>
      )}
      <div className="measurements-list">
        {showAddMeasurementForm && (
          <AddMeasurementForm
            doc={doc}
            onConfirm={handleAddMeasurement}
            onCancel={() => setShowAddMeasurementForm(false)}
          />
        )}
        {renderMeasurements()}
      </div>
    </Tile>
  );

  return (
    <div className="document-detail-tiles">
      {measurementsTile}
    </div>
  );
};

export default MeasurementsTab;
